/*Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  Table,
  Spinner,
  PageNavigationButtons,
  SpanWithData,
  FileName,
  Warning
} from '../'

import {
  decodeBase64,
  getNodeTypeFromBase64Id,
  PageNavigationControl,
  getPageCursor
} from '../../utils'

const RIGHT_ARROW = <span style={{ fontSize: 20 }}>&rarr;</span>  // TODO: componentise

class ObjectFiles extends React.PureComponent {
  static propTypes = {
    object: PropTypes.string.isRequired,
    onFilesFetched: PropTypes.func,
    resultsPerPage: PropTypes.number,
    // TODO: specify which fetched fields to match to corresponding column names to be displayed instead of default
    // columns: PropTypes.arrayOf(PropTypes.shape({
    //   field: PropTypes.string.isRequired,
    //   columnName: PropTypes.string.isRequired
    // })),
    renderAs: PropTypes.oneOf([ 'list', 'table' ]),
    triggerRefetch: PropTypes.oneOfType([  // prop can be used to pass unique strings into in order to trigger a data refetch
      PropTypes.object,
      PropTypes.string,
    ]),
    noRecordsToDisplay: PropTypes.any,
    className: PropTypes.string
  }

  static defaultProps = {
    renderAs: 'table',
    triggerRefetch: null,
    resultsPerPage: 15,
    className: '',
    noRecordsToDisplay: null
  }

  state = {
    refetch: false,
    pageNav: new PageNavigationControl()
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.triggerRefetch !== this.props.triggerRefetch) {
      this.props.refetch()
    } else {
      if (nextProps.error && !this.state.refetch) {
        this.setState({
          refetch: true
        })
        nextProps.refetch()
      }

      if (this.state.refetch) {
        this.setState({
          refetch: false
        })
      }

      if (nextProps.filerelations) {
        this.state.pageNav.setPageStates(this.props.filerelations, nextProps.filerelations)
      }
    }
  }

  render() {
    if (this.props.loading) {
      return <Spinner type='brand' className='slds-is-relative' />
    }

    if (this.props.error) {
      return (
        <Warning className={this.props.className ? this.props.className : ''}>
          Error loading data.
        </Warning>
      )
    }

    if (this.props.filerelations.edges.length === 0) {
      return this.props.noRecordsToDisplay
    } else {
      return (
        <div className={this.props.className ? this.props.className : ''}>
          {this.props.renderAs === 'table'
            ? <FilesTable
                fileRelations={this.props.filerelations.edges}
                startCursor={this.props.filerelations.pageInfo.startCursor}
                totalCount={this.props.filerelations.totalCount}
                resultsPerPage={this.props.resultsPerPage}
                onClick={file => this.props.history.push(`/os/operations/general/files/${file}`)}
                pageNav={this.state.pageNav}
                fetchNext={this.props.fetchNext}
                fetchPrevious={this.props.fetchPrevious}
              />
            : <FilesList fileRelations={this.props.filerelations.edges} onClick={this.props.onClick} />
          }
        </div>
      )
    }
  }
}

const FilesList = ({ fileRelations, onClick }) => (
  fileRelations.map(fileRelation => {
    let fileId = fileRelation.node.file.id
    return (
      <div className='slds-col slds-size_1-of-1' key={`file_${fileId}`}>
        <FileName
          id={fileId}
          onClick={onClick}
        />
      </div>
    )
  })
)

const FilesTable = ({ fileRelations, startCursor, totalCount, resultsPerPage, onClick, pageNav, fetchNext, fetchPrevious }) => (
  <React.Fragment>
    <Table
      columns={["Type", "Created", "Source", "File name", "Processed", ""]}
      rows={fileRelations.map(fileRelation => {
        let file = fileRelation.node.file
        return (
          <tr onClick={() => onClick(file.id)} key={`row_${file.id}`}>
            <td>
              {file.filetype ? file.filetype.name : '-'}
            </td>
            <td>
              {moment(file.createdAt).format("lll")}
            </td>
            <td>
              {<SpanWithData forField="FileSource" searchKey={file.source} />}
            </td>
            <td>
              {file.asset.includes('/')
                ? file.asset
                  .substr(file.asset.lastIndexOf('/') + 1)  // last part of file asset url (not really necessary if not other '__' in asset name)
                  .split('__').pop()  // get part after '__'
                : file.asset
              }
            </td>
            <td>
              {file.processed ? "Yes" : "No"}
            </td>
            <td>
              <a style={{ textDecoration: "none" }}>
                {RIGHT_ARROW}
              </a>
            </td>
          </tr>
        )
      })}
      objectRows={true}
    />
    <PageNavigationButtons
      onNextClick={() => pageNav.nextPage(fetchNext, resultsPerPage)}
      onPreviousClick={() => pageNav.previousPage(fetchPrevious, resultsPerPage)}
      previousDisabled={pageNav.firstPage}
      nextDisabled={pageNav.lastPage}
      startCursor={startCursor}
      totalCount={totalCount}
      resultsPerPage={resultsPerPage}
      resultsText='records'
      onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, resultsPerPage) })}
      className="slds-p-around_medium"
    />
  </React.Fragment>
)

const getFileRelations = gql`
  query($object: UUID, $model: String!, $resultsPerPage: Int!, $cursor: String) {
    filerelations(contentType_Model: $model, objectId: $object, orderBy: "-created_at", first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          createdAt
          file {
            id
            createdAt
            source
            asset
            url
            processed
            filetype {
              id
              name
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousFileRelations = gql`
  query($object: UUID, $model: String!, $resultsPerPage: Int!, $cursor: String) {
    filerelations(contentType_Model: $model, objectId: $object, orderBy: "-created_at", last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          createdAt
          file {
            id
            createdAt
            source
            asset
            url
            processed
            filetype {
              id
              name
            }
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData = graphql(getFileRelations, {
    options: ({ object, model, resultsPerPage = 15 }) => {
      let nodeType = getNodeTypeFromBase64Id(object)
      return {
        variables: {
          object: decodeBase64(object),
          model: nodeType.toLowerCase().replace('node', ''),
          resultsPerPage
        },
        fetchPolicy: 'network-only'
      }
    },
    props: ({ ownProps: { object, model }, data: { loading, error, refetch, filerelations, fetchMore } }) => {
      let objectId = decodeBase64(object)
      let modelName = getNodeTypeFromBase64Id(object).toLowerCase().replace('node', '')
      return {
        loading,
        error,
        refetch,
        filerelations,
        fetchNext: (resultsPerPage) => {
          return fetchMore({
            query: getFileRelations,
            variables: {
              object: objectId,
              model: modelName,
              resultsPerPage,
              cursor: filerelations.pageInfo.endCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.filerelations.edges
              const pageInfo = fetchMoreResult.filerelations.pageInfo
              return {
                filerelations: {
                  __typename: previousResult.filerelations.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        },
        fetchPrevious: (resultsPerPage) => {
          return fetchMore({
            query: getPreviousFileRelations,
            variables: {
              object: objectId,
              model: modelName,
              resultsPerPage,
              cursor: filerelations.pageInfo.startCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.filerelations.edges
              const pageInfo = fetchMoreResult.filerelations.pageInfo
              return {
                filerelations: {
                  __typename: previousResult.filerelations.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        }
      }
    }
  })

export default compose(
  withData,
  withRouter
)(ObjectFiles)



// WEBPACK FOOTER //
// ./src/components/object-files/index.js